import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import api from "../../config/api";

const BillingList = () => {
  const [billingDocs, setBillingDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedBatches, setExpandedBatches] = useState({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const user = useSelector((state) => state.auth.user);

  // Fetch billing documents
  const fetchBillingDocs = async () => {
    setLoading(true);
    try {
      const response = await api.get("/api/billing");

      if (response.data.success) {
        setBillingDocs(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching billing docs:", error);
      toast.error("Failed to load billing documents", {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBillingDocs();
  }, []);

  // Delete billing document - open modal
  const handleDelete = (id) => {
    setDocToDelete(id);
    setDeleteModalOpen(true);
  };

  // Confirm deletion
  const confirmDelete = async () => {
    if (!docToDelete) return;

    try {
      const response = await api.delete(`/api/billing/${docToDelete}`);

      if (response.data.success) {
        toast.success("Document deleted successfully!", {
          position: "top-right",
        });
        fetchBillingDocs();
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error(error.response?.data?.error || "Failed to delete document", {
        position: "top-right",
      });
    } finally {
      setDeleteModalOpen(false);
      setDocToDelete(null);
    }
  };

  // Cancel deletion
  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setDocToDelete(null);
  };


  // View/Download file
  const handleView = (fileUrl) => {
    window.open(fileUrl, "_blank");
  };

  // Download file directly
  const handleDownload = async (fileUrl, fileName) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Download started!", {
        position: "top-right",
      });
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file", {
        position: "top-right",
      });
    }
  };

  // Download all files in a batch
  const handleDownloadBatch = async (files) => {
    for (const file of files) {
      await handleDownload(file.file_url, file.file_name);
    }
  };

  // Toggle batch expansion
  const toggleBatch = (batchId) => {
    setExpandedBatches(prev => ({
      ...prev,
      [batchId]: !prev[batchId]
    }));
  };

  // Check if user is from Accounting department
  const isAccountingUser = user?.department === 'Accounting';

  // Filter billing docs by search term
  const filteredDocs = billingDocs.filter((doc) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesBasic =
      doc.title.toLowerCase().includes(searchLower) ||
      doc.file_name.toLowerCase().includes(searchLower) ||
      doc.uploaded_by.toLowerCase().includes(searchLower);

    // For accounting users, also search by recipient name
    if (isAccountingUser && doc.recipient) {
      const recipientName = `${doc.recipient.first_name} ${doc.recipient.last_name}`.toLowerCase();
      const recipientUsername = doc.recipient.user_name?.toLowerCase() || '';
      return matchesBasic || recipientName.includes(searchLower) || recipientUsername.includes(searchLower);
    }

    return matchesBasic;
  });

  // Group documents by batch_id
  const groupedDocs = filteredDocs.reduce((acc, doc) => {
    const key = doc.batch_id || `single_${doc.id}`;
    if (!acc[key]) {
      acc[key] = {
        isBatch: !!doc.batch_id,
        files: [],
        title: doc.title,
        description: doc.description,
        uploaded_by: doc.uploaded_by,
        uploaded_at: doc.uploaded_at,
        recipient: doc.recipient
      };
    }
    acc[key].files.push(doc);
    return acc;
  }, {});

  // Convert to array and sort by date
  const groupedDocsArray = Object.entries(groupedDocs)
    .map(([key, value]) => ({ key, ...value }))
    .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));

  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'fa-file-pdf text-red-500';
      case 'xlsx':
      case 'xls':
        return 'fa-file-excel text-green-600';
      case 'csv':
        return 'fa-file-csv text-blue-500';
      default:
        return 'fa-file text-gray-500';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';

    try {
      // Handle PostgreSQL timestamp format
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date parsing error:', error, dateString);
      return 'Invalid Date';
    }
  };

  // Render a single file row
  const renderFileRow = (doc, isNested = false) => (
    <tr
      key={doc.id}
      className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${isNested ? 'bg-gray-50/50' : ''}`}
    >
      <td className="py-4 px-4">
        <div className={`flex items-center gap-3 ${isNested ? 'pl-8' : ''}`}>
          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
            <i className={`fas ${getFileIcon(doc.file_name)} text-xl`}></i>
          </div>
          <span className="text-sm text-gray-600">{doc.file_name}</span>
        </div>
      </td>
      <td className="py-4 px-4">
        <p className="font-medium text-gray-800">{doc.title}</p>
      </td>
      <td className="py-4 px-4">
        <p className="text-sm text-gray-600 max-w-xs truncate">
          {doc.description || '-'}
        </p>
      </td>
      {isAccountingUser && (
        <td className="py-4 px-4">
          {doc.recipient ? (
            <div>
              <p className="text-sm font-medium text-gray-800">
                {doc.recipient.first_name} {doc.recipient.last_name}
              </p>
              <p className="text-xs text-gray-500">{doc.recipient.department}</p>
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">No recipient</p>
          )}
        </td>
      )}
      <td className="py-4 px-4">
        <p className="text-sm text-gray-600">{doc.uploaded_by}</p>
      </td>
      <td className="py-4 px-4">
        <p className="text-sm text-gray-600">
          {formatDate(doc.uploaded_at)}
        </p>
      </td>
      <td className="py-4 px-4">
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => handleView(doc.file_url)}
            className="px-3 py-2 bg-blue text-white rounded-lg hover:bg-blue/90 transition-all duration-300 text-sm"
            title="View"
          >
            <i className="fas fa-eye"></i>
          </button>
          <button
            onClick={() => handleDownload(doc.file_url, doc.file_name)}
            className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-300 text-sm"
            title="Download"
          >
            <i className="fas fa-download"></i>
          </button>
          <button
            onClick={() => handleDelete(doc.id)}
            className="px-3 py-2 bg-red text-white rounded-lg hover:bg-red/90 transition-all duration-300 text-sm"
            title="Delete"
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </td>
    </tr>
  );

  // Render a batch group row
  const renderBatchGroup = (group) => {
    const isExpanded = expandedBatches[group.key];
    const fileCount = group.files.length;

    return (
      <React.Fragment key={group.key}>
        {/* Batch header row */}
        <tr
          className="border-b border-gray-200 bg-blue/5 cursor-pointer hover:bg-blue/10 transition-colors"
          onClick={() => toggleBatch(group.key)}
        >
          <td className="py-4 px-4">
            <div className="flex items-center gap-3">
              <button className="w-6 h-6 flex items-center justify-center text-blue">
                <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} text-sm transition-transform`}></i>
              </button>
              <div className="w-10 h-10 bg-blue/10 rounded-lg flex items-center justify-center">
                <i className="fas fa-folder text-xl text-blue"></i>
              </div>
              <div>
                <span className="text-sm font-medium text-blue">{fileCount} files</span>
              </div>
            </div>
          </td>
          <td className="py-4 px-4">
            <p className="font-medium text-gray-800">{group.title}</p>
          </td>
          <td className="py-4 px-4">
            <p className="text-sm text-gray-600 max-w-xs truncate">
              {group.description || '-'}
            </p>
          </td>
          {isAccountingUser && (
            <td className="py-4 px-4">
              {group.recipient ? (
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    {group.recipient.first_name} {group.recipient.last_name}
                  </p>
                  <p className="text-xs text-gray-500">{group.recipient.department}</p>
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">No recipient</p>
              )}
            </td>
          )}
          <td className="py-4 px-4">
            <p className="text-sm text-gray-600">{group.uploaded_by}</p>
          </td>
          <td className="py-4 px-4">
            <p className="text-sm text-gray-600">
              {formatDate(group.uploaded_at)}
            </p>
          </td>
          <td className="py-4 px-4">
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadBatch(group.files);
                }}
                className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-300 text-sm"
                title="Download All"
              >
                <i className="fas fa-download mr-1"></i>
                All
              </button>
            </div>
          </td>
        </tr>
        {/* Expanded files */}
        {isExpanded && group.files.map(doc => renderFileRow(doc, true))}
      </React.Fragment>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8 font-poppins">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-blue mb-2">Billing Documents</h1>
          <p className="text-gray-600">
            Manage and view all billing documents
          </p>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-3xl shadow-xl p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <i className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder={
                  isAccountingUser
                    ? "Search by title, file name, uploaded by, or recipient..."
                    : "Search by title, file name, or uploaded by..."
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue focus:outline-none transition-all duration-300"
              />
            </div>
          </div>
        </div>

        {/* Billing Documents List */}
        <div className="bg-white rounded-3xl shadow-xl p-8">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <i className="fas fa-spinner fa-spin text-4xl text-blue"></i>
            </div>
          ) : groupedDocsArray.length === 0 ? (
            <div className="text-center py-12">
              <i className="fas fa-folder-open text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-lg">No billing documents found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">File</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">Title</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">Description</th>
                    {isAccountingUser && (
                      <th className="text-left py-4 px-4 font-semibold text-gray-700">Recipient</th>
                    )}
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">Uploaded By</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">Date</th>
                    <th className="text-center py-4 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedDocsArray.map((group) =>
                    group.isBatch && group.files.length > 1
                      ? renderBatchGroup(group)
                      : group.files.map(doc => renderFileRow(doc))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <i className="fas fa-exclamation-triangle text-red-500 text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Delete Document</h3>
            </div>

            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this document? This action cannot be undone.
            </p>

            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDelete}
                className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingList;
